import React, { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { SendIcon, MessageCircleIcon, ArrowRightIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
export function Consultation() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const {
    t
  } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setFormData({
        name: '',
        email: '',
        message: ''
      });
    }, 1500);
  };
  return <section id="consultation" ref={ref} className="relative w-full overflow-hidden px-4 py-16 sm:py-24" style={{
    backgroundColor: 'var(--bg-primary)'
  }} data-id="element-30">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden opacity-30" data-id="element-31">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full blur-3xl" style={{
        background: 'radial-gradient(circle, var(--accent) 0%, transparent 70%)'
      }} data-id="element-32" />
      </div>

      <div className="relative z-10 mx-auto max-w-3xl" data-id="element-33">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="mb-10 sm:mb-12 text-center" data-id="element-34">
          <h2 className="mb-4 text-3xl font-bold md:text-4xl" style={{
          color: 'var(--text-primary)'
        }} data-id="element-35">
            {t('consultation.title')}{' '}
            <span style={{
            color: 'var(--accent)'
          }} data-id="element-36">
              {t('consultation.titleHighlight')}
            </span>
          </h2>
          <p className="mx-auto max-w-xl text-sm sm:text-base" style={{
          color: 'var(--text-secondary)'
        }} data-id="element-37">
            {t('consultation.subtitle')}
          </p>
        </motion.div>

        {/* Form Card */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        delay: 0.2
      }} data-id="element-38">
          <div className="rounded-3xl border p-6 sm:p-10 relative overflow-hidden" style={{
          borderColor: 'var(--border)',
          backgroundColor: 'var(--bg-secondary)'
        }} data-id="element-39">
            {/* Decorative corner accents */}
            <div className="absolute top-0 right-0 w-32 h-32 opacity-5" style={{
            background: 'radial-gradient(circle at top right, var(--accent), transparent 70%)'
          }} data-id="element-40" />
            <div className="absolute bottom-0 left-0 w-32 h-32 opacity-5" style={{
            background: 'radial-gradient(circle at bottom left, var(--accent), transparent 70%)'
          }} data-id="element-41" />

            <form onSubmit={handleSubmit} className="space-y-5 relative z-10" data-id="element-42">
              <div className="grid sm:grid-cols-2 gap-5" data-id="element-43">
                <div data-id="element-44">
                  <label htmlFor="name" className="mb-2 block text-sm font-medium" style={{
                  color: 'var(--text-secondary)'
                }} data-id="element-45">
                    {t('consultation.name')}
                  </label>
                  <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required className="w-full rounded-xl border px-4 py-3.5 text-sm sm:text-base transition-all focus:outline-none focus:ring-2 focus:ring-blue-500/30" style={{
                  borderColor: 'var(--border)',
                  backgroundColor: 'var(--bg-tertiary)',
                  color: 'var(--text-primary)'
                }} placeholder={t('consultation.namePlaceholder')} data-id="element-46" />
                </div>

                <div data-id="element-47">
                  <label htmlFor="email" className="mb-2 block text-sm font-medium" style={{
                  color: 'var(--text-secondary)'
                }} data-id="element-48">
                    {t('consultation.email')}
                  </label>
                  <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required className="w-full rounded-xl border px-4 py-3.5 text-sm sm:text-base transition-all focus:outline-none focus:ring-2 focus:ring-blue-500/30" style={{
                  borderColor: 'var(--border)',
                  backgroundColor: 'var(--bg-tertiary)',
                  color: 'var(--text-primary)'
                }} placeholder={t('consultation.emailPlaceholder')} data-id="element-49" />
                </div>
              </div>

              <div data-id="element-50">
                <label htmlFor="message" className="mb-2 block text-sm font-medium" style={{
                color: 'var(--text-secondary)'
              }} data-id="element-51">
                  {t('consultation.desc')}
                </label>
                <textarea id="message" name="message" value={formData.message} onChange={handleChange} required rows={4} className="w-full resize-none rounded-xl border px-4 py-3.5 text-sm sm:text-base transition-all focus:outline-none focus:ring-2 focus:ring-blue-500/30" style={{
                borderColor: 'var(--border)',
                backgroundColor: 'var(--bg-tertiary)',
                color: 'var(--text-primary)'
              }} placeholder={t('consultation.descPlaceholder')} data-id="element-52" />
              </div>

              <motion.button type="submit" disabled={isSubmitting} whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} className="group w-full rounded-xl py-4 font-semibold transition-all disabled:cursor-not-allowed disabled:opacity-70 text-sm sm:text-base" style={{
              backgroundColor: 'var(--accent)',
              color: '#FFFFFF'
            }} data-id="element-53">
                <span className="flex items-center justify-center gap-2" data-id="element-54">
                  {isSubmitting ? t('consultation.sending') : <>
                      {t('consultation.cta')}
                      <ArrowRightIcon className="h-5 w-5 transition-transform group-hover:translate-x-1" data-id="element-55" />
                    </>}
                </span>
              </motion.button>

              <p className="text-xs text-center" style={{
              color: 'var(--text-muted)'
            }} data-id="element-56">
                {t('consultation.note')}
              </p>
            </form>

            <div className="my-8 flex items-center gap-4" data-id="element-57">
              <div className="h-px flex-1" style={{
              backgroundColor: 'var(--border)'
            }} data-id="element-58" />
              <span className="text-xs sm:text-sm" style={{
              color: 'var(--text-muted)'
            }} data-id="element-59">
                {t('consultation.or')}
              </span>
              <div className="h-px flex-1" style={{
              backgroundColor: 'var(--border)'
            }} data-id="element-60" />
            </div>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4" data-id="element-61">
              <motion.a href="https://t.me/jetlyagency" target="_blank" rel="noopener noreferrer" whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} className="flex flex-1 items-center justify-center gap-3 rounded-xl py-3.5 font-semibold transition-all border text-sm sm:text-base" style={{
              borderColor: 'var(--border)',
              backgroundColor: 'var(--bg-tertiary)',
              color: 'var(--text-primary)'
            }} data-id="element-62">
                <SendIcon className="h-4 w-4 sm:h-5 sm:w-5" style={{
                color: 'var(--accent)'
              }} data-id="element-63" />
                {t('consultation.telegram')}
              </motion.a>

              <motion.a href="https://wa.me/1234567890" target="_blank" rel="noopener noreferrer" whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} className="flex flex-1 items-center justify-center gap-3 rounded-xl py-3.5 font-semibold transition-all border text-sm sm:text-base" style={{
              borderColor: 'var(--border)',
              backgroundColor: 'var(--bg-tertiary)',
              color: 'var(--text-primary)'
            }} data-id="element-64">
                <MessageCircleIcon className="h-4 w-4 sm:h-5 sm:w-5" style={{
                color: 'var(--accent)'
              }} data-id="element-65" />
                {t('consultation.whatsapp')}
              </motion.a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}